import { createAction } from '@reduxjs/toolkit';

import errorTypes from '../../constants/errorTypes';
import { urlUtils } from '../../utils/url';
import { fetchLabels } from '../../services/content';
import { fetchFlightTime } from '../../services/flight';
import { getProductsIncluded } from '../../utils/mappers/productsIncluded';
import { fetchPriceMatrix, addPackageToCart } from '../../services/package';
import { addHotelOnlyToCart, fetchHotelOnlyPriceMatrix } from '../../services/hotelOnly';
import { fetchPriceMatrix as fetchCombinationPriceMatrix } from '../../services/combination';

import * as ExponeaUtils from '../../utils/logUtils/ExponeaUtils';
import * as PriceMatrixLogUtils from '../../utils/logUtils/PriceMatrixLogUtils';

// Everything a thunk dispatches lives here; `roomGroupSelected` is the only container-only action
// and is slice-generated on `hotelData`.
export const labelsLoaded = createAction('priceMatrix/labelsLoaded');
export const initSucceeded = createAction('priceMatrix/initSucceeded');
export const initFailed = createAction('priceMatrix/initFailed');
export const errorPopupToggled = createAction('priceMatrix/errorPopupToggled');
export const addToCartStarted = createAction('priceMatrix/addToCartStarted');
export const addToCartFailed = createAction('priceMatrix/addToCartFailed');
export const addToCartSucceeded = createAction('priceMatrix/addToCartSucceeded');
export const flightTimeLoaded = createAction('priceMatrix/flightTimeLoaded');

// NB: No reducer reads this one — the old bare `INIT`, dispatched and ignored. Kept so the
//     conversion does not quietly remove a store notification; a deletion candidate once someone
//     confirms nothing wants it. Same as combo-summary's.
export const initStarted = createAction('priceMatrix/initStarted');

const log = (data) => {
  const message = data?.translatedMessage;
  const loggingData = data?.result?.loggingData;

  if (!loggingData) return;

  const isInvalidSearchDate =
    loggingData.departureDateDay === '01' &&
    loggingData.departureDateMonth === '01' &&
    loggingData.departureDateYear === '1';

  if (isInvalidSearchDate) return;

  PriceMatrixLogUtils.priceMatrixSearch(loggingData, message);
  ExponeaUtils.logSearch(loggingData, 'Geographical/HotelInfo');
};

const getLabels = async (siteSettings) => {
  const promises = [fetchLabels(siteSettings, 'iwPriceMatrix'), fetchLabels(siteSettings, 'iwHotelList')];

  const [labels, alternativeHotelsLabels] = await Promise.all(promises);
  const { general, ...rest } = labels;

  return {
    ...rest,
    ...general,
    alternativeHotelsLabels,
  };
};

const getPriceMatrix = async (siteSettings, userId) => {
  const isCombination = urlUtils.isCombination();
  const isHotelOnly = urlUtils.getTypeFromUrl() === 'IndependentHotelOnly';

  let response;
  switch (true) {
    case isCombination:
      response = await fetchCombinationPriceMatrix(siteSettings, userId);
      break;
    case isHotelOnly:
      response = await fetchHotelOnlyPriceMatrix(siteSettings, userId);
      break;
    default:
      response = await fetchPriceMatrix(siteSettings, userId);
      break;
  }

  return response;
};

// The body of the old `initSuccess` thunk, kept as a helper taking `dispatch` rather than inlined,
// so `init` stays readable. Three exits, in order:
//
//   1. a non-200 response fails immediately, before the popup, the logging, the URL write or the
//      labels — so on that path `searchInformation` stays null and `transportData` becomes
//      `{ initialized: false }` from a null state;
//   2. no room groups plus a translated message fails after all of those, carrying the
//      alternative hotels only when there are some;
//   3. otherwise it succeeds.
const applyInitOutcome = (dispatch, response, labels) => {
  const isCombination = urlUtils.isCombination();

  if (response?.statusCode !== 200) {
    dispatch(initFailed({ message: response.translatedMessage }));
    return;
  }

  const { result } = response;
  const sessionHasExpired = result?.serviceCallInformation?.sessionHasExpired;

  if (sessionHasExpired) {
    dispatch(
      errorPopupToggled({
        visible: true,
        errorType: errorTypes.SESSION_EXPIRED,
        buttonUrl: isCombination ? result?.summaryNoSessionLink : null,
      }),
    );
  }

  log(response);

  const updateUrl = result != null && result.searchInformation != null;

  if (updateUrl && !sessionHasExpired) {
    urlUtils.updateUrlWithPriceMatrixSearchResultParams(result);
  }

  const { general, alternativeHotelsLabels, ...rest } = labels;

  dispatch(
    labelsLoaded({
      labels: {
        ...rest,
        ...general,
        productsIncludedParsed: getProductsIncluded(result?.packageTag, labels),
      },
      alternativeHotelsLabels: {
        ...alternativeHotelsLabels,
        ...alternativeHotelsLabels.general,
        productsIncludedParsed: getProductsIncluded(result?.packageTag, labels),
      },
    }),
  );

  if (!result.roomGroups && response.translatedMessage) {
    dispatch(
      initFailed({
        message: response.translatedMessage,
        ...(result?.alternativeHotels?.length > 0 && {
          alternativeHotels: result.alternativeHotels,
          searchInformation: result.searchInformation,
          transportData: result.transport,
        }),
      }),
    );
    return;
  }

  dispatch(
    initSucceeded({
      alternativeHotels: result.alternativeHotels,
      hotelData: result.hotelInformation,
      message: response.translatedMessage,
      roomGroups: result.roomGroups,
      searchInformation: result.searchInformation,
      summaryReturnLink: result.summaryReturnLink,
      transportData: result.transport,
      roomGroupKeyFromUrl: urlUtils.getRoomGroupKeyFromUrl(),
    }),
  );
};

// Stays a hand-written thunk, per the D1 refinement: three terminal outcomes, and the two failure
// paths differ in what they carry, which one fulfilled/rejected pair cannot express.
export const init = () => async (dispatch, getState) => {
  const { siteSettings, userId } = getState().common;

  dispatch(initStarted());

  const [labels, priceMatrixResponse] = await Promise.all([
    getLabels(siteSettings),
    getPriceMatrix(siteSettings, userId),
  ]);

  applyInitOutcome(dispatch, priceMatrixResponse, labels);
};

export const addToCart = () => async (dispatch, getState) => {
  const { common, hotelData, transportData } = getState();
  const { siteSettings, userId } = common;
  const { selectedRoomGroup } = hotelData;
  const { outBoundFlights, homeBoundFlights } = transportData ?? {};
  const { bookingEngineRoomGroupKey: accommodationKey, link } = selectedRoomGroup;
  const isHotelOnly = urlUtils.getTypeFromUrl() === 'IndependentHotelOnly';

  const sessionId = urlUtils.getSessionIdFromUrl();

  let model = {
    SiteId: siteSettings.siteId,
    SessionId: `${sessionId}`,
    UserId: userId,
    AccomodationKey: accommodationKey,
  };
  if (!isHotelOnly) {
    model = {
      ...model,
      homeboundTransportKey: homeBoundFlights[0].bookingEngineKey,
      outboundTransportKey: outBoundFlights[0].bookingEngineKey,
    };
  }

  dispatch(addToCartStarted());

  const addToCartResponse = isHotelOnly
    ? await addHotelOnlyToCart(model, siteSettings)
    : await addPackageToCart(model, siteSettings);
  const { result } = addToCartResponse;

  if (!result.success) {
    dispatch(addToCartFailed());

    if (result.sessionHasExpired) {
      dispatch(
        errorPopupToggled({
          errorType: errorTypes.SESSION_EXPIRED,
          visible: true,
        }),
      );
      return;
    }

    if (addToCartResponse.translatedMessage) {
      dispatch(
        errorPopupToggled({
          errorType: errorTypes.SYSTEM_ERROR_TRANSLATED,
          visible: true,
          title: addToCartResponse.translatedMessage.title,
          message: addToCartResponse.translatedMessage.message,
        }),
      );
      return;
    }

    if (addToCartResponse.errorMessage) {
      dispatch(
        errorPopupToggled({
          errorType: errorTypes.SYSTEM_ERROR,
          visible: true,
          message: addToCartResponse.errorMessage,
        }),
      );
    }

    return;
  }

  dispatch(addToCartSucceeded());

  window.location = `/${link}`;
};

export const getFlightTime = () => async (dispatch, getState) => {
  const { common, transportData } = getState();
  const { homeBoundFlights, outBoundFlights } = transportData;
  const { siteSettings } = common;

  const sessionId = urlUtils.getSessionIdFromUrl();

  const response = await fetchFlightTime(
    sessionId,
    homeBoundFlights[0].bookingEngineKey,
    outBoundFlights[0].bookingEngineKey,
    siteSettings,
  );

  dispatch(flightTimeLoaded(response?.result));
};
